import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * kitchen `Select` 로 조건부 필수 상세 입력이 필수일 때, 옵션을 "선택할 때마다"
 * 해당 상세 입력란(textarea)으로 커서를 자동 이동시킨다. 이때 커서는 기존 텍스트 끝(최우측)에 둔다.
 * (정책: "설정 시 커서 자동 활성화")
 *
 * ── 왜 선택값이 아니라 선택 "이벤트"에 반응하나 ────────────────────────
 * kitchen `Select` 는 이미 선택된 옵션을 다시 클릭해도 `onChange` 를 발화한다.
 * 하지만 재선택 시 넘어오는 값은 이전과 동일하므로, 선택값(참조)의 변화로 포커스를
 * 트리거하면 동일 값 재선택에서 커서가 이동하지 않는다(LQAE-4230).
 * 그래서 선택값 대신, 선택 때마다 증가하는 시퀀스(`notifySelection`)로 effect 를 구동한다.
 *
 * ── 왜 포커스를 effect(rAF)로 예약하나 ─────────────────────────────
 * kitchen `Select` 는 드롭다운을 닫으면서 `useEffect([showOptions])` 안의
 * `requestAnimationFrame` 으로 트리거(SelectBox)에 포커스를 되돌린다. 그래서 Select
 * 변경 핸들러에서 곧바로 `focus()` 를 예약하면, 우리 rAF 가 핸들러(=리렌더 前)에서
 * "먼저" 등록되어 나중에 도는 Select rAF 에 덮인다.
 *
 * 이 훅은 포커스 예약을 "부모(모달) 레벨 effect" 로 옮긴다. `Select` 는 모달의 자손이고
 * React 는 자식 effect → 부모 effect 순으로 실행하므로, 우리 rAF 가 Select rAF 보다
 * "나중에" 등록되어 최종적으로 우리 포커스가 승리한다. 추가로 rAF 콜백에서
 * `document.activeElement` 를 확인해, 우리 입력란이 아니면 한 번 더 `focus()` 한다.
 *
 * ⚠️ kitchen 이 포커스 복귀를 "단일 rAF" 로 처리한다는 현재 구현에 결합된다.
 *    kitchen 이 이중 rAF / setTimeout / 동기 focus 로 바뀌면 이 훅을 재검토해야 한다.
 *    (근본책은 kitchen `Select` 에 "선택 후 포커스 복귀 억제" 옵션을 추가하는 것)
 * ──────────────────────────────────────────────────────────────
 *
 * @param required 상세 입력이 필수로 전환됐는지 여부. false 면 포커스하지 않는다.
 * @returns
 *  - `ref`: 상세 입력란 `Textarea` 에 연결할 ref.
 *  - `notifySelection`: Select 변경 핸들러에서 선택이 일어날 때마다 호출한다.
 *    (동일 값 재선택 포함) 마운트 시에는 호출되지 않으므로 자동 포커스도 없다.
 */
const useAutoFocusOnRequired = (required: boolean) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [selectSeq, setSelectSeq] = useState(0);

  const notifySelection = useCallback(() => setSelectSeq((seq) => seq + 1), []);

  useEffect(() => {
    // 마운트(초기 seq=0)에는 포커스하지 않는다. 선택이 일어난(seq>0) 뒤,
    // 그 선택 결과가 상세 필수일 때만 커서를 이동한다.
    if (selectSeq === 0 || !required) return;

    const rafId = window.requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      if (document.activeElement !== el) el.focus();
      // textarea.focus()는 직전 selection 위치로 커서를 되돌려 기존 문구 앞(최좌측)에 놓일 수 있으므로,
      // 커서를 명시적으로 텍스트 끝(최우측)으로 이동한다(LQAE-4238).
      const caretToEnd = el.value.length;
      el.setSelectionRange(caretToEnd, caretToEnd);
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [selectSeq, required]);

  return { ref, notifySelection };
};

export default useAutoFocusOnRequired;
